'use client';

import { formatDate, formatDateWithOptionalYear } from '@/utils/date';
import { currencySymbolForJPY, formatJPY } from '@/utils/number';
import { useTranslations } from 'next-intl';
import { JobOfferFragment } from '../gen/graphql/JobRoute.generated';
import clsx from 'clsx';

export interface JobOfferListItemProps {
  offer: JobOfferFragment;
  onClick: (offer: JobOfferFragment) => void;
}

export function JobOfferListItem({ offer, onClick }: JobOfferListItemProps) {
  const t = useTranslations('job');
  const date = new Date(offer.workTimeStartAt);
  const day = formatDateWithOptionalYear(date);
  const weekday = formatDate(date, 'weekday');
  const from = formatDate(date, 'time');
  const to = formatDate(new Date(offer.workTimeEndAt), 'time');
  const canApply = offer.isOffering;
  const isSunday = date.getDay() === 0;
  const isSaturday = date.getDay() === 6;

  return (
    <div
      role={canApply ? 'button' : undefined}
      className="flex flex-col p-3 border-thin border-neutral-auxiliary rounded-medium min-w-[80px] pc:min-w-[146px]"
      onClick={() => canApply && onClick(offer)}
    >
      <p className="text-small text-neutral-primary font-bold">
        {t.rich('offerListItem.day', {
          day,
          weekday,
          dayContainer: (chunks) => <span className="text-medium">{chunks}</span>,
          weekdayContainer: (chunks) => <span className="text-x-small">{chunks}</span>,
          weekdayContent: (chunks) => (
            <span
              className={clsx(
                'text-x-small',
                isSaturday && 'text-brand-primary',
                isSunday && 'text-semantic-red'
              )}
            >
              {chunks}
            </span>
          ),
        })}
      </p>
      <p className="text-x-small text-neutral-primary mb-1">
        {t('offerListItem.time', {
          from,
          to,
        })}
      </p>
      {canApply ? (
        <p className="text-small text-brand-primary">
          <span className="text-style-num-x-small">{currencySymbolForJPY()}</span>
          <span className="text-style-num-small">{formatJPY(offer.payPerHour)}</span>
        </p>
      ) : (
        <p className="bg-neutral-auxiliary-tinge rounded-small flex flex-1 items-center justify-center text-minute text-neutral-primary py-2">
          {t('offerListItem.expired')}
        </p>
      )}
    </div>
  );
}
